import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { queryClient } from '../queryClient';

export const PENDING_BUSINESSES_QUERY_KEY = ['businesses', 'submitted'] as const;
export const PENDING_BUSINESSES_COUNT_QUERY_KEY = ['businesses', 'submitted-count'] as const;
const DIRECTORY_QUERY_KEY = ['businesses', 'connect-businesses-page'] as const;

const PENDING_FILTER = 'approved.is.null,approved.eq.false';

export interface PendingBusiness {
  id: string;
  name: string;
  description?: string | null;
  website?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  approved?: boolean | null;
  created_at?: string | null;
}

const invalidatePendingBusinessQueries = () => {
  queryClient.invalidateQueries({ queryKey: PENDING_BUSINESSES_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: PENDING_BUSINESSES_COUNT_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: DIRECTORY_QUERY_KEY });
};

export const usePendingBusinessesCount = (enabled = true) =>
  useQuery<number>({
    queryKey: PENDING_BUSINESSES_COUNT_QUERY_KEY,
    queryFn: async () => {
      const { count, error } = await supabase
        .from('businesses')
        .select('id', { count: 'exact', head: true })
        .or(PENDING_FILTER);
      if (error) throw error;
      return count ?? 0;
    },
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

export const usePendingBusinesses = (enabled = true) =>
  useQuery<PendingBusiness[]>({
    queryKey: PENDING_BUSINESSES_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('businesses')
        .select('id, name, description, website, address, phone, email, approved, created_at')
        .or(PENDING_FILTER)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as PendingBusiness[];
    },
    enabled,
  });

export const useApprovePendingBusiness = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('businesses').update({ approved: true }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidatePendingBusinessQueries,
  });

export const useRejectPendingBusiness = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('businesses').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidatePendingBusinessQueries,
  });
